import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api, { API_ENDPOINTS } from '../config/api';
import BlogCardMedium from '../components/BlogCardMedium';
import BlogPreviewColumns from '../components/BlogPreviewColumns';
import BlogCardOverflow from '../components/BlogCardOverflow';
import { Loader2, ChevronLeft, ChevronRight } from 'lucide-react';

const CategoryPage = () => {
  const { category } = useParams();
  const [latestPage, setLatestPage] = useState(1);
  const [allPage, setAllPage] = useState(1);
  const limit = 21; // Same as Trending page

  // Capitalize category name for API
  const categoryName = category.charAt(0).toUpperCase() + category.slice(1);

  // Fetch featured blogs (single row with overflow)
  const { data: featuredData } = useQuery({
    queryKey: ['blogs', 'category', categoryName, 'featured'],
    queryFn: async () => {
      const response = await api.get(API_ENDPOINTS.BLOGS.LIST, {
        params: { category: categoryName, filter: 'featured', limit: 10 },
      });
      return response.data;
    },
  });

  // Fetch latest blogs
  const { data: latestData, isLoading: latestLoading } = useQuery({
    queryKey: ['blogs', 'category', categoryName, 'latest', latestPage],
    queryFn: async () => {
      const response = await api.get(API_ENDPOINTS.BLOGS.LIST, {
        params: { category: categoryName, page: latestPage, limit },
      });
      return response.data;
    },
  });

  // Fetch all stories
  const { data: allData, isLoading: allLoading } = useQuery({
    queryKey: ['blogs', 'category', categoryName, 'all', allPage],
    queryFn: async () => {
      const response = await api.get(API_ENDPOINTS.BLOGS.LIST, {
        params: { category: categoryName, page: allPage, limit },
      });
      return response.data;
    },
  });

  if (latestLoading || allLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  const featuredBlogs = featuredData?.blogs || [];
  const latestBlogs = latestData?.blogs || [];
  const allBlogs = allData?.blogs || [];
  const latestPagination = latestData?.pagination || {};
  const allPagination = allData?.pagination || {};
  const latestTotalPages = latestPagination.totalPages || Math.ceil((latestPagination.total || latestBlogs.length) / limit);
  const allTotalPages = allPagination.totalPages || Math.ceil((allPagination.total || allBlogs.length) / limit);

  // Split blogs like Trending page: 12 cards overflow + 9 for columns
  const latestFirstRow = latestBlogs.slice(0, 12);
  const latestColumns = latestBlogs.slice(12, 21);
  const allFirstRow = allBlogs.slice(0, 12);
  const allColumns = allBlogs.slice(12, 21);

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-4xl font-heading font-bold text-heading dark:text-white mb-8">
          {categoryName}
        </h1>

        {/* Featured Section - Single row with overflow */}
        {featuredBlogs.length > 0 && (
          <section className="mb-16">
            <h2 className="text-2xl font-heading font-bold text-heading dark:text-white mb-6">
              Featured
            </h2>
            <BlogCardOverflow blogs={featuredBlogs} showCount={3} />
          </section>
        )}

        {/* Latest Section - Like Trending Page */}
        <section className="mb-16">
          <h2 className="text-2xl font-heading font-bold text-heading dark:text-white mb-6">
            Latest
          </h2>
          
          {/* First Row: 12 cards with overflow */}
          {latestFirstRow.length > 0 && (
            <div className="mb-12">
              <BlogCardOverflow blogs={latestFirstRow} showCount={3} />
            </div>
          )}

          {/* Second Row: 3 columns with 9 preview cards */}
          {latestColumns.length > 0 && (
            <div className="mb-12">
              <BlogPreviewColumns blogs={latestColumns} />
            </div>
          )}

          {/* Pagination for Latest */}
          {latestTotalPages > 1 && (
            <div className="flex items-center justify-center space-x-2 mt-12">
              <button
                onClick={() => setLatestPage((p) => Math.max(1, p - 1))}
                disabled={latestPage === 1}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </button>
              <span className="px-4 py-2">
                Page {latestPage} of {latestTotalPages}
              </span>
              <button
                onClick={() => setLatestPage((p) => Math.min(latestTotalPages, p + 1))}
                disabled={latestPage === latestTotalPages}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center"
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </button>
            </div>
          )}
        </section>

        {/* All Stories Section - Like Trending Page */}
        <section className="mb-16">
          <h2 className="text-2xl font-heading font-bold text-heading dark:text-white mb-6">
            All Stories
          </h2>
          
          {/* First Row: 12 cards with overflow */}
          {allFirstRow.length > 0 && (
            <div className="mb-12">
              <BlogCardOverflow blogs={allFirstRow} showCount={3} />
            </div>
          )}

          {/* Second Row: 3 columns with 9 preview cards */}
          {allColumns.length > 0 && (
            <div className="mb-12">
              <BlogPreviewColumns blogs={allColumns} />
            </div>
          )}

          {/* Pagination for All Stories */}
          {allTotalPages > 1 && (
            <div className="flex items-center justify-center space-x-2 mt-12">
              <button
                onClick={() => setAllPage((p) => Math.max(1, p - 1))}
                disabled={allPage === 1}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </button>
              <span className="px-4 py-2">
                Page {allPage} of {allTotalPages}
              </span>
              <button
                onClick={() => setAllPage((p) => Math.min(allTotalPages, p + 1))}
                disabled={allPage === allTotalPages}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center"
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </button>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default CategoryPage;
